/* The reaction picker, the footer's chip stack, and the "who reacted" dialog.
 *
 * All three are here rather than in PostCard because a Prayer Request card and
 * a notification's post preview will want the same surfaces, and a second copy
 * of the picker is how two rows of reactions drift apart.
 *
 * HOVER OPENS IT, ON A MOUSE. That is the web's version of the phone's long
 * press, and for the same reason: the plain click has to stay instant, because
 * it is the action people take most, so the choice sits behind a gesture
 * rather than a second click. A touch screen has no hover, so there the
 * gesture IS a long press — see useReactionTrigger.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react'
import { Loader2, ThumbsUp } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Modal } from '@/components/ui/modal'
import { cn, compactCount } from '@/lib/utils'
import { usePostReactions, useReactionKinds } from '@/api/queries'
import type { Post } from '@/types/api'

/** What a reader can say back, beyond a thumbs-up.
 *
 *  Chosen for a church feed: the same button carried "congratulations on the
 *  baptism" and "I read your mother's diagnosis", and a thumbs-up under the
 *  second reads as careless — so people who wanted to answer a hard post said
 *  nothing instead.
 *
 *  Deliberately five, and deliberately nothing jokey. There is no laugh and no
 *  wow: a feed carrying somebody's bereavement is not a place to be flippant,
 *  and the moment such a reaction exists it lands on the wrong post.
 *
 *  The server serves this same list at /reactions and is the authority. The
 *  copy here is what the picker draws before that call returns, so it never
 *  opens empty.
 */
export const REACTIONS = [
  { kind: 'like', label: 'Like', emoji: '👍' },
  { kind: 'pray', label: 'Pray', emoji: '🙏' },
  // AMEN — what a congregation says aloud to a testimony or an answered
  // prayer. Agreement and blessing in one word, and the most common response
  // in church life; the set without it was assembled from what social products
  // offer rather than from what these people actually say.
  { kind: 'amen', label: 'Amen', emoji: '🕊️' },
  { kind: 'heart', label: 'Heart', emoji: '❤️' },
  { kind: 'praise', label: 'Praise', emoji: '🙌' },
] as const

export function reactionByKind(kind: string | null | undefined) {
  return REACTIONS.find((r) => r.kind === kind) ?? null
}

export function emojiFor(kind: string): string {
  return reactionByKind(kind)?.emoji ?? ''
}

/** Where a kind sits in the offered order. Unknown kinds sort last rather than
 *  crashing — a server that adds one before the app ships still draws. */
function orderOf(kind: string): number {
  const i = REACTIONS.findIndex((r) => r.kind === kind)
  return i < 0 ? REACTIONS.length : i
}

/** The kinds actually on this post, most-used first.
 *
 *  Ties break on the offered order rather than on whatever the object happened
 *  to enumerate, so two posts with the same counts stack their chips the same
 *  way round.
 */
export function topReactions(post: Post): string[] {
  return Object.keys(post.reaction_counts ?? {})
    .filter((k) => (post.reaction_counts[k] ?? 0) > 0)
    .sort((a, b) => {
      const byCount =
        (post.reaction_counts[b] ?? 0) - (post.reaction_counts[a] ?? 0)
      return byCount !== 0 ? byCount : orderOf(a) - orderOf(b)
    })
}

/** A tinted disc behind the emoji, so the stack reads as a row of marks rather
 *  than floating glyphs. Only Like is brand blue — it is the one the card has
 *  always drawn that way. */
const CHIP_FILL: Record<string, string> = {
  like: 'bg-brand',
  pray: 'bg-brand-soft',
  amen: 'bg-success-soft',
  heart: 'bg-danger-soft',
  praise: 'bg-warning-soft',
}

/* ------------------------------------------------------------ the trigger */

/** Hover on a mouse, long press on a touch screen — one hook for both.
 *
 *  WHY BOTH. Hover is what a person with a mouse expects here, and it is what
 *  every desktop feed does. But a touch screen fires no hover at all, and
 *  falling back to "tap to open the picker" would cost the plain Like its
 *  instant tap. So a hold opens it there, exactly as the phone app does.
 *
 *  The close delay is the fiddly part: the picker sits ABOVE the button with a
 *  gap the pointer must cross, and closing the moment the button is left makes
 *  it impossible to reach. Leaving the picker itself closes it at once.
 */
export function useReactionTrigger() {
  const [open, setOpen] = useState(false)
  const closeTimer = useRef<number | null>(null)
  const holdTimer = useRef<number | null>(null)
  /** Set by a long press, so the click it also fires can be swallowed —
   *  otherwise opening the picker by holding ALSO likes the post. */
  const held = useRef(false)

  const clearTimers = useCallback(() => {
    if (closeTimer.current) window.clearTimeout(closeTimer.current)
    if (holdTimer.current) window.clearTimeout(holdTimer.current)
    closeTimer.current = null
    holdTimer.current = null
  }, [])

  useEffect(() => clearTimers, [clearTimers])

  return {
    open,
    close: () => {
      clearTimers()
      setOpen(false)
    },
    /** Spread onto the Like button. */
    triggerProps: {
      onPointerEnter: (e: ReactPointerEvent) => {
        // Mouse only. A touch "enters" on tap, which would open the picker on
        // every plain like.
        if (e.pointerType !== 'mouse') return
        clearTimers()
        setOpen(true)
      },
      onPointerLeave: (e: ReactPointerEvent) => {
        if (e.pointerType !== 'mouse') return
        closeTimer.current = window.setTimeout(() => setOpen(false), 260)
      },
      onPointerDown: (e: ReactPointerEvent) => {
        if (e.pointerType === 'mouse') return
        held.current = false
        holdTimer.current = window.setTimeout(() => {
          held.current = true
          setOpen(true)
        }, 420)
      },
      onPointerUp: (e: ReactPointerEvent) => {
        if (e.pointerType === 'mouse') return
        if (holdTimer.current) window.clearTimeout(holdTimer.current)
      },
      onPointerCancel: () => clearTimers(),
    },
    /** Wraps the button's own click so a long press does not ALSO fire it.
     *
     *  Kept out of triggerProps deliberately: those are spread straight onto
     *  the button, and anything in there that is not a DOM attribute lands in
     *  the HTML as one. */
    guardClick: (run: () => void) => () => {
      if (held.current) {
        held.current = false
        return
      }
      run()
    },
    /** Spread onto the picker itself, so crossing into it keeps it open. */
    surfaceProps: {
      onPointerEnter: () => clearTimers(),
      onPointerLeave: (e: ReactPointerEvent) => {
        if (e.pointerType !== 'mouse') return
        setOpen(false)
      },
    },
  }
}

/* ------------------------------------------------------------- the picker */

/** The row that opens above a hovered or held Like button.
 *
 *  Choosing the kind already chosen is meaningful — it clears it, which is what
 *  pressing your own reaction again should do — so the caller sends whatever
 *  comes back rather than filtering it.
 */
export function ReactionPicker({
  selected,
  onPick,
  surfaceProps,
}: {
  selected: string | null
  onPick: (kind: string) => void
  surfaceProps?: React.ComponentProps<'div'>
}) {
  const served = useReactionKinds()
  // The served list is the authority; the local copy draws until it lands, so
  // the row never appears empty.
  const kinds = served.data?.length ? served.data : REACTIONS

  return (
    <div
      {...surfaceProps}
      role="group"
      aria-label="How do you want to respond?"
      className={cn(
        'absolute bottom-full left-0 z-40 mb-1 flex items-end gap-1',
        'rounded-pill border border-line-2 bg-surface px-s2 py-1.5 shadow-3',
        'animate-fade-in',
      )}
    >
      {kinds.map((r) => (
        <button
          key={r.kind}
          type="button"
          onClick={() => onPick(r.kind)}
          title={r.label}
          aria-label={r.label}
          aria-pressed={r.kind === selected}
          className={cn(
            'grid h-10 w-10 place-items-center rounded-pill text-[22px] leading-none',
            'transition-transform duration-DEFAULT hover:scale-125 focus-visible:outline-none focus-visible:shadow-focus',
            // The one already chosen is ringed, so a second pass shows what
            // pressing it again would clear.
            r.kind === selected && 'bg-brand-soft ring-2 ring-brand',
          )}
        >
          <span aria-hidden>{r.emoji}</span>
        </button>
      ))}
    </div>
  )
}

/* --------------------------------------------------------- the chip stack */

/** The overlapping chips and the total, as every social product draws it.
 *
 *  CAPPED AT THREE: past that they stop reading as distinct marks at this size
 *  and the row starts pushing the comment count off the card. The number
 *  beside them is the server's true total regardless, so a post with five
 *  kinds shows three marks and an honest number.
 */
export function ReactionSummary({
  post,
  onOpen,
}: {
  post: Post
  onOpen: () => void
}) {
  const kinds = topReactions(post).slice(0, 3)
  const total = post.reaction_total

  if (total <= 0) return null

  return (
    <button
      type="button"
      onClick={onOpen}
      className="flex items-center gap-1.5 rounded-sm py-0.5 hover:underline"
      aria-label={`${total} ${total === 1 ? 'reaction' : 'reactions'}, see who`}
    >
      <span
        className="relative inline-block h-[18px]"
        // Each extra chip adds only its exposed sliver, or the gap before the
        // number would grow with the overlap.
        style={{ width: 18 + Math.max(0, kinds.length - 1) * 12 }}
        aria-hidden
      >
        {/* Drawn in reverse so the FIRST kind — the one most people chose —
            sits on top of the pile. */}
        {kinds
          .map((kind, i) => (
            <span
              key={kind}
              style={{ left: i * 12, zIndex: kinds.length - i }}
              className={cn(
                'absolute top-0 grid h-[18px] w-[18px] place-items-center rounded-pill text-[10px] leading-none',
                // The ring is what separates one chip from the next when they
                // overlap, and it must be the CARD's colour rather than white,
                // or it shows as a halo on a tinted card.
                'ring-[1.5px] ring-surface',
                CHIP_FILL[kind] ?? 'bg-surface-2',
              )}
            >
              {/* Like keeps its drawn icon; the rest carry their emoji. A
                  thumbs-up glyph at this size reads as a smudge where the icon
                  is crisp — and the four newer kinds have no icon that means
                  what they mean. */}
              {kind === 'like' ? (
                <ThumbsUp className="h-2.5 w-2.5 fill-white text-white" />
              ) : (
                emojiFor(kind)
              )}
            </span>
          ))
          .reverse()}
      </span>
      {compactCount(total)}
    </button>
  )
}

/* -------------------------------------------------------- who reacted */

/** Who reacted, with a tab per kind that anybody actually used. */
export function ReactorsDialog({
  post,
  onClose,
}: {
  post: Post
  onClose: () => void
}) {
  // Only the kinds actually on this post get a tab. Five tabs where three are
  // always empty is a row of dead ends. null is the All tab.
  const kinds: (string | null)[] = [null, ...topReactions(post)]
  const [active, setActive] = useState<string | null>(null)

  const reactions = usePostReactions(post.id, active)
  const people = reactions.data?.pages.flatMap((p) => p.items) ?? []

  function tabLabel(kind: string | null) {
    if (kind === null) return `All ${post.reaction_total}`
    return `${emojiFor(kind)} ${post.reaction_counts[kind] ?? 0}`
  }

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      title="Reactions"
      width="440px"
    >
      <div
        role="tablist"
        aria-label="Reactions by kind"
        className="flex gap-s1 overflow-x-auto border-b border-line-2 px-s4"
      >
        {kinds.map((kind) => (
          <button
            key={kind ?? 'all'}
            role="tab"
            aria-selected={active === kind}
            onClick={() => setActive(kind)}
            className={cn(
              'shrink-0 border-b-2 px-s3 py-s2 text-base font-bold transition-colors',
              active === kind
                ? 'border-brand text-brand'
                : 'border-transparent text-ink-2 hover:text-ink',
            )}
          >
            {tabLabel(kind)}
          </button>
        ))}
      </div>

      <div className="min-h-[220px] p-s2">
        {reactions.isLoading ? (
          <div className="grid h-[200px] place-items-center">
            <Loader2
              className="h-5 w-5 animate-spin text-ink-3"
              aria-label="Loading"
            />
          </div>
        ) : null}

        {reactions.isError ? (
          <div className="p-s6 text-center">
            <p className="font-semibold">That list did not load.</p>
            <p className="mt-1 text-base text-ink-2">
              {(reactions.error as Error)?.message}
            </p>
            <button
              onClick={() => reactions.refetch()}
              className="mt-s3 text-base font-semibold text-brand hover:underline"
            >
              Try again
            </button>
          </div>
        ) : null}

        {!reactions.isLoading && !reactions.isError && !people.length ? (
          <div className="p-s6 text-center">
            <p className="text-md font-bold">Nobody yet</p>
            <p className="mt-1 text-base text-ink-2">Be the first to respond.</p>
          </div>
        ) : null}

        <ul>
          {people.map((person) => (
            <li
              key={`${person.user_id}-${person.kind}`}
              className="flex items-center gap-s3 rounded px-s2 py-s2"
            >
              <Avatar name={person.name} src={person.avatar_url} />
              <span className="min-w-0 flex-1 truncate text-md">
                {person.name}
              </span>
              {/* The kind rides on the row, so the All tab says what each
                  person chose without making anyone switch tabs to find out. */}
              <span className="text-lg" title={reactionByKind(person.kind)?.label}>
                {emojiFor(person.kind)}
              </span>
            </li>
          ))}
        </ul>

        {reactions.hasNextPage ? (
          <button
            onClick={() => reactions.fetchNextPage()}
            disabled={reactions.isFetchingNextPage}
            className="mt-s2 px-s2 text-sm font-semibold text-brand hover:underline"
          >
            {reactions.isFetchingNextPage ? 'Loading…' : 'See more'}
          </button>
        ) : null}
      </div>
    </Modal>
  )
}
